import { useState } from 'react';
import { Screen } from '../components/AppShell';
import { Icon, Skeleton, Sheet, Field, StatusPill, Badge } from '../components/ui';
import { api } from '../lib/api';
import { useAction, useLoad } from '../lib/hooks';
import { useAuth } from '../store/auth';
import { formatDate } from '../../../shared/schedule';
import './screens.css';

interface StaffUser {
  id: string;
  email: string;
  name: string;
  position: string | null;
  role: 'owner' | 'admin' | 'inspector';
  active: number;
  last_login_at: string | null;
  created_at: string;
}

const ROLE_HELP: Record<string, string> = {
  inspector: 'Can run and sign inspections, log defects and jobs.',
  admin: 'Everything an inspector can do, plus staff, customers and amendments.',
  owner: 'Full control, including other owners.',
};

export default function Staff() {
  const me = useAuth((s) => s.user);
  const { data, loading, error, reload } = useLoad<{ users: StaffUser[] }>(() => api.get('/auth/users'));
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState<StaffUser | null>(null);

  return (
    <Screen
      onRefresh={reload}
      title="Staff"
      subtitle="Who can sign inspections"
      back="/more"
      action={
        <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
          <Icon name="plus" size={17} />
          Add
        </button>
      }
    >
      <div className="stack-4">
        {error ? <div className="notice notice-danger">{error}</div> : null}
        {loading && !data ? <Skeleton rows={5} /> : null}

        {data && (
          <div className="panel divide">
            {data.users.map((user) => (
              <div key={user.id} className="listrow" style={{ alignItems: 'flex-start' }}>
                <span className="menu-icon">
                  <Icon name="user" size={19} />
                </span>
                <div className="grow">
                  <div className="listrow-title">
                    {user.name}
                    {user.id === me?.id ? <span className="dim"> · you</span> : null}
                  </div>
                  <div className="listrow-sub truncate">
                    {user.position ?? 'No position'}
                    <span className="dot" />
                    {user.email}
                  </div>
                  <div className="row row-tight" style={{ marginTop: 6 }}>
                    <Badge tone={user.role === 'owner' ? 'hazard' : 'neutral'}>{user.role}</Badge>
                    {!user.active ? (
                      <StatusPill state="overdue" size="sm">
                        Disabled
                      </StatusPill>
                    ) : null}
                    {user.last_login_at ? (
                      <span className="tiny dim">last in {formatDate(user.last_login_at.slice(0, 10))}</span>
                    ) : (
                      <span className="tiny dim">never signed in</span>
                    )}
                  </div>
                </div>
                {user.id !== me?.id ? (
                  <button className="btn btn-quiet btn-sm" onClick={() => setResetting(user)}>
                    Manage
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}

        <p className="hint">
          Every signature is stored against the account that made it, with a timestamp. Give each person
          their own login rather than sharing one.
        </p>
      </div>

      <AddStaff
        open={adding}
        onClose={() => setAdding(false)}
        onDone={() => {
          setAdding(false);
          reload();
        }}
      />
      <ManageStaff
        user={resetting}
        onClose={() => setResetting(null)}
        onDone={() => {
          setResetting(null);
          reload();
        }}
      />
    </Screen>
  );
}

function AddStaff({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { busy, error, run } = useAction();
  const [form, setForm] = useState({ name: '', position: '', email: '', password: '', role: 'inspector' });

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = () =>
    run(async () => {
      await api.post('/auth/users', form);
      setForm({ name: '', position: '', email: '', password: '', role: 'inspector' });
      onDone();
    });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Add someone"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={busy || !form.name || !form.email || form.password.length < 10}
          >
            {busy ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      <div className="stack-4">
        {error ? <div className="notice notice-danger">{error}</div> : null}
        <Field label="Name">
          <input className="input" value={form.name} onChange={set('name')} />
        </Field>
        <Field label="Position" hint="Printed under their signature.">
          <input className="input" value={form.position} onChange={set('position')} placeholder="HGV Technician" />
        </Field>
        <Field label="Email">
          <input className="input" type="email" autoCapitalize="off" value={form.email} onChange={set('email')} />
        </Field>
        <Field label="Temporary password" hint="At least 10 characters. They can change it once signed in.">
          <input className="input" value={form.password} onChange={set('password')} />
        </Field>
        <Field label="Role" hint={ROLE_HELP[form.role]}>
          <select className="select" value={form.role} onChange={set('role')}>
            <option value="inspector">Inspector</option>
            <option value="admin">Admin</option>
          </select>
        </Field>
      </div>
    </Sheet>
  );
}

function ManageStaff({
  user,
  onClose,
  onDone,
}: {
  user: StaffUser | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { busy, error, run } = useAction();
  const [password, setPassword] = useState('');

  if (!user) return null;

  const setActive = (active: boolean) =>
    run(async () => {
      await api.patch(`/auth/users/${user.id}`, { active });
      onDone();
    });

  const setRole = (role: string) =>
    run(async () => {
      await api.patch(`/auth/users/${user.id}`, { role });
      onDone();
    });

  const resetPassword = () =>
    run(async () => {
      await api.post(`/auth/users/${user.id}/set-password`, { password });
      setPassword('');
      onDone();
    });

  return (
    <Sheet open onClose={onClose} title={user.name}>
      <div className="stack-4">
        {error ? <div className="notice notice-danger">{error}</div> : null}

        <Field label="Role" hint={ROLE_HELP[user.role]}>
          <select className="select" value={user.role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
            <option value="inspector">Inspector</option>
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
          </select>
        </Field>

        <Field label="Set a new password" hint="At least 10 characters. Signs them out everywhere.">
          <input className="input" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <button className="btn btn-ghost" onClick={resetPassword} disabled={busy || password.length < 10}>
          Set password
        </button>

        <hr style={{ border: 'none', borderTop: '1px solid var(--line)' }} />

        {user.active ? (
          <>
            <button className="btn btn-danger" onClick={() => setActive(false)} disabled={busy}>
              Disable this account
            </button>
            <p className="hint">
              They can no longer sign in. Inspections they have already signed stay on record — those
              cannot be removed.
            </p>
          </>
        ) : (
          <button className="btn btn-ghost" onClick={() => setActive(true)} disabled={busy}>
            Re-enable this account
          </button>
        )}
      </div>
    </Sheet>
  );
}
